<template>
  <div class="relative mobile-controls-container" ref="dropdownRef">
    <!-- Trigger Button (Apple Style Pill) -->
    <button
      type="button"
      @click="toggleMenu"
      class="mobile-trigger-btn"
      :class="{
        'mobile-trigger-btn--open': isOpen,
      }"
      title="Quick Menu"
      aria-label="Quick Menu"
    >
      <span v-if="currentSkin" class="mobile-skin-tag truncate">
        {{ currentSkin }}
      </span>

      <div class="mobile-trigger-icon">
        <i class="fa-solid fa-shapes"></i>
      </div>

      <i
        class="fa-solid fa-chevron-down mobile-trigger-chevron"
        :class="{ 'rotate-180': isOpen }"
      ></i>
    </button>

    <!-- Quick Menu Dropdown Panel (Apple Style) -->
    <transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="transform scale-95 opacity-0 -translate-y-2"
      enter-to-class="transform scale-100 opacity-100 translate-y-0"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="transform scale-100 opacity-100 translate-y-0"
      leave-to-class="transform scale-95 opacity-0 -translate-y-2"
    >
      <div v-if="isOpen" class="mobile-quickmenu-panel shadow-2xl" role="menu">
        <!-- Panel Header -->
        <div class="mobile-panel-header">
          <div class="flex items-center gap-2">
            <div class="header-icon-box">
              <i class="fa-solid fa-layer-group"></i>
            </div>
            <h4 class="header-title">Quick Menu</h4>
          </div>
          <button
            @click="closeMenu"
            class="header-close-btn"
            title="Close menu"
            type="button"
          >
            <i class="fa-solid fa-xmark text-xs"></i>
          </button>
        </div>

        <!-- Scrollable Menu Content -->
        <div class="mobile-panel-body">
          <!-- 1. Categories Section (Opens Desktop Mega Menu) -->
          <div
            v-if="allowedCategories.length > 1"
            @click.stop="handleCategoriesClick"
            class="menu-card"
          >
            <div class="flex items-center gap-2.5 min-w-0">
              <div class="menu-card-icon-box">
                <i class="fa-solid fa-shapes"></i>
              </div>
              <div class="text-left min-w-0">
                <div class="menu-card-title flex items-center gap-1.5">
                  <span>Categories</span>
                  <span class="count-badge">{{
                    allowedCategories.length
                  }}</span>
                </div>
                <div class="menu-card-desc truncate">
                  {{ allowedCategories.map((c) => c.title).join(", ") }}
                </div>
              </div>
            </div>
            <i class="fa-solid fa-chevron-right text-xs text-slate-400"></i>
          </div>

          <!-- 2. Skin Selection Card -->
          <div @click.stop="handleSkinsClick" class="menu-card">
            <div class="flex items-center gap-2.5 min-w-0">
              <div class="menu-card-icon-box">
                <i class="fa-solid fa-globe"></i>
              </div>
              <div class="text-left min-w-0">
                <div class="menu-card-title">Skin Selection</div>
                <div class="menu-card-desc truncate">
                  Active:
                  <strong class="text-slate-800">{{
                    currentSkin || "All Skins"
                  }}</strong>
                </div>
              </div>
            </div>
            <span class="action-pill">Change</span>
          </div>

          <!-- 4. Crash Games (if enabled) -->
          <a
            v-if="hasCrash"
            :href="urlCrash"
            title="Crash Games"
            @click="closeMenu"
            class="menu-card no-underline text-inherit"
          >
            <div class="flex items-center gap-2.5 min-w-0">
              <div class="menu-card-icon-box">
                <i class="fa-solid fa-plane-departure"></i>
              </div>
              <div class="text-left min-w-0">
                <div class="menu-card-title">Crash Games</div>
                <div class="menu-card-desc">Go to Crash Games</div>
              </div>
            </div>
            <i
              class="fa-solid fa-arrow-up-right-from-square text-xs text-slate-400"
            ></i>
          </a>

          <!-- 5. Platform Providers (Accordion) -->
          <div
            class="menu-card-accordion"
            :class="{ 'menu-card-accordion--open': isProviderOpen }"
          >
            <div @click="toggleProviderMenu" class="menu-card-header">
              <div class="flex items-center gap-2.5 min-w-0">
                <div class="provider-logo-container">
                  <div class="overlay-providers"></div>
                  <img
                    :src="selectedProvider.logo"
                    :alt="selectedProvider.label"
                    class="provider-logo-img"
                  />
                </div>
                <div class="text-left min-w-0">
                  <div class="menu-card-title flex items-center gap-1.5">
                    <span>{{ selectedProvider.label }}</span>
                  </div>
                  <div class="menu-card-desc truncate">
                    Provider: {{ selectedProvider.sublabel }}
                  </div>
                </div>
              </div>

              <i
                class="fa-solid fa-chevron-down card-chevron"
                :class="{ 'rotate-180': isProviderOpen }"
              ></i>
            </div>

            <!-- Providers Content List -->
            <div v-show="isProviderOpen" class="accordion-content space-y-1.5">
              <div
                v-for="provider in PROVIDERS"
                :key="provider.id"
                @click="handleSelectProvider(provider.id)"
                class="provider-choice-card"
                :class="{
                  'provider-choice-card--active':
                    provider.id === selectedProvider.id,
                }"
              >
                <div class="flex items-center gap-2.5 min-w-0">
                  <div class="provider-choice-logo-box">
                    <div class="overlay-providers"></div>
                    <img
                      :src="provider.logo"
                      :alt="provider.label"
                      class="provider-choice-img"
                    />
                  </div>
                  <div class="text-left min-w-0">
                    <div class="text-xs font-bold text-slate-800">
                      {{ provider.label }}
                    </div>
                    <div class="text-[10px] text-slate-500">
                      {{
                        provider.id === selectedProvider.id
                          ? "Active"
                          : "Switch"
                      }}
                    </div>
                  </div>
                </div>

                <div
                  v-if="provider.id === selectedProvider.id"
                  class="provider-selected-badge"
                >
                  <i class="fa-solid fa-circle-check"></i>
                  <span>Active</span>
                </div>
                <i
                  v-else
                  class="fa-solid fa-chevron-right text-slate-400 text-xs"
                ></i>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import {
  getActiveDatabase,
  toggleLiveCasinoFilter,
  clearActiveDatabase,
} from "../helpers/databaseMapping";
import {
  PROVIDERS,
  getActiveProvider,
  switchProvider,
} from "../helpers/provider";
import {
  categoriesMenuConfig,
  buildNavigationRoute,
} from "@/config/categoriesMenu";
import { isCategoryAllowed, getUrlCrash } from "@/helpers/appConfiguration";

const props = defineProps({
  urlCrash: {
    type: String,
    default: "",
  },
  hasCrash: {
    type: Boolean,
    default: true,
  },
  hasLiveCasino: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits([
  "open-categories",
  "open-skins",
  "open-utilities",
  "select-game",
]);

const route = useRoute();
const store = useStore();
const isOpen = ref(false);
const isLiveCasinoOpen = ref(false);
const isProviderOpen = ref(false);
const dropdownRef = ref(null);

const allowedCategories = computed(() => {
  return categoriesMenuConfig.categories.filter((cat) =>
    isCategoryAllowed(cat.section),
  );
});

const selectedProvider = computed(() => getActiveProvider());

const isLiveCasinoActive = computed(() => {
  return store
    ? store.getters.isLiveCasinoActive
    : getActiveDatabase() === "livecasino";
});

const currentSkin = computed(() => {
  return route.params.skinFilt || store?.state?.skinStorage || "";
});

const toggleMenu = () => {
  isOpen.value = !isOpen.value;
  if (!isOpen.value) {
    isLiveCasinoOpen.value = false;
    isProviderOpen.value = false;
  }
};

const closeMenu = () => {
  isOpen.value = false;
  isLiveCasinoOpen.value = false;
  isProviderOpen.value = false;
};

const handleCategoriesClick = (event) => {
  if (event && typeof event.stopPropagation === "function") {
    event.stopPropagation();
  }
  closeMenu();
  setTimeout(() => {
    emit("open-categories");
  }, 50);
};

const getRouteLocation = (item) => {
  return buildNavigationRoute(item, route, store);
};

const toggleProviderMenu = () => {
  isProviderOpen.value = !isProviderOpen.value;
};

const handleSelectProvider = (providerId) => {
  closeMenu();
  switchProvider(providerId);
};

const toggleLiveCasino = () => {
  isLiveCasinoOpen.value = !isLiveCasinoOpen.value;
};

const handleSkinsClick = (event) => {
  if (event && typeof event.stopPropagation === "function") {
    event.stopPropagation();
  }
  closeMenu();
  setTimeout(() => {
    emit("open-skins");
  }, 50);
};

const handleRouletteClick = () => {
  closeMenu();
  if (store) {
    store.dispatch("toggleLiveCasinoFilter", "livecasino");
  } else {
    toggleLiveCasinoFilter("livecasino");
  }
  emit("select-game", "roulette");
  window.location.reload();
};

const handleClearLiveCasino = () => {
  closeMenu();
  if (store) {
    store.commit("CLEAR_LIVE_CASINO_FILTER");
  } else {
    clearActiveDatabase();
  }
  window.location.reload();
};

const handleUtilitiesClick = () => {
  closeMenu();
  emit("open-utilities");
};

const handleClickOutside = (event) => {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target)) {
    closeMenu();
  }
};

const handleKeyDown = (event) => {
  if (event.key === "Escape" && isOpen.value) {
    closeMenu();
  }
};

// Body Class management for custom CSS & operator customization
watch(
  isOpen,
  (val) => {
    if (typeof document !== "undefined") {
      if (val) {
        document.body.classList.add("mobile-quickmenu-open");
        document.body.classList.add("megamenu-open");
      } else {
        document.body.classList.remove("mobile-quickmenu-open");
        document.body.classList.remove("megamenu-open");
      }
    }
  },
  { immediate: true },
);

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
  document.addEventListener("keydown", handleKeyDown);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
  document.removeEventListener("keydown", handleKeyDown);
  if (typeof document !== "undefined") {
    document.body.classList.remove("mobile-quickmenu-open");
    document.body.classList.remove("megamenu-open");
  }
});
</script>

<style lang="scss" scoped>
.mobile-controls-container {
  display: inline-flex;
  align-items: center;
}

/* Trigger Button */
.mobile-trigger-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.4rem 0.75rem;
  border-radius: 9999px;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  color: #1e293b;
  font-weight: 600;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
  user-select: none;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

  .mobile-skin-tag {
    font-size: 0.65rem;
    font-weight: 700;
    padding: 1px 6px;
    border-radius: 6px;
    background: #e8e8ed;
    color: #48484a;
    max-width: 65px;
  }

  .mobile-trigger-icon {
    width: 24px;
    height: 24px;
    border-radius: 6px;
    background: #f5f5f7;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--primary-color, #386f99);
    font-size: 0.75rem;
  }

  .mobile-trigger-chevron {
    font-size: 0.65rem;
    color: #64748b;
    transition: transform 0.25s ease;
  }

  &:hover {
    background: #f8fafc;
    border-color: var(--primary-color, #386f99);
  }

  &--open {
    border-color: var(--primary-color, #386f99);
    box-shadow: 0 0 0 3px rgba(56, 111, 153, 0.16);
    background: #f8fafc;
  }
}

/* Mobile Quick Menu Dropdown Panel (Apple Style) */
.mobile-quickmenu-panel {
  position: absolute;
  right: 0;
  top: calc(100% + 8px);
  width: 320px;
  max-width: 90vw;
  background: #ffffff;
  border-radius: 18px;
  border-top: 3px solid var(--primary-color, #386f99);
  border-left: 1px solid rgba(0, 0, 0, 0.06);
  border-right: 1px solid rgba(0, 0, 0, 0.06);
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 20px 40px -10px rgba(15, 23, 42, 0.18),
    0 4px 16px rgba(15, 23, 42, 0.06);
  z-index: 1050;
  overflow: hidden;
}

/* Panel Header */
.mobile-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  background: #f5f5f7;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);

  .header-icon-box {
    width: 26px;
    height: 26px;
    border-radius: 7px;
    background: #ffffff;
    border: 1px solid rgba(0, 0, 0, 0.06);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--primary-color, #386f99);
    font-size: 0.75rem;
  }

  .header-title {
    font-size: 0.85rem;
    font-weight: 700;
    color: #1d1d1f;
    letter-spacing: -0.01em;
    margin: 0;
  }

  .header-close-btn {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: #e8e8ed;
    color: #6e6e73;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    cursor: pointer;
    transition: all 0.18s ease;

    &:hover {
      background: #d1d1d6;
      color: #1d1d1f;
    }
  }
}

/* Panel Body */
.mobile-panel-body {
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: min(78vh, 560px);
  overflow-y: auto;
  overscroll-behavior: contain;

  &::-webkit-scrollbar {
    width: 5px;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
  }
}

/* Menu Card (Apple Style) */
.menu-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.65rem 0.85rem;
  background: #f5f5f7;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.04);
  cursor: pointer;
  transition: all 0.18s ease;
  flex-shrink: 0;
  min-height: 48px;

  &:hover {
    background: #e8e8ed;
  }

  .menu-card-icon-box {
    width: 34px;
    height: 34px;
    border-radius: 8px;
    background: #ffffff;
    border: 1px solid rgba(0, 0, 0, 0.05);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--primary-color, #386f99);
    font-size: 0.85rem;
    flex-shrink: 0;
  }

  .menu-card-title {
    font-size: 0.825rem;
    font-weight: 700;
    color: #1d1d1f;
    line-height: 1.2;
  }

  .menu-card-desc {
    font-size: 0.68rem;
    color: #86868b;
    margin-top: 1px;
    line-height: 1.2;
  }

  .action-pill {
    font-size: 0.65rem;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 9999px;
    background: #ffffff;
    border: 1px solid #d1d5db;
    color: #475569;
    text-transform: uppercase;
  }
}

/* Accordion Card */
.menu-card-accordion {
  border-radius: 12px;
  background: #f5f5f7;
  border: 1px solid rgba(0, 0, 0, 0.04);
  overflow: hidden;
  transition: all 0.18s ease;
  flex-shrink: 0;

  &--open {
    background: #ffffff;
    border-color: #cbd5e1;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  }

  .menu-card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.65rem 0.85rem;
    cursor: pointer;
    user-select: none;
    transition: background 0.18s ease;

    &:hover {
      background: #e8e8ed;
    }

    .menu-card-icon-box {
      width: 34px;
      height: 34px;
      border-radius: 8px;
      background: #ffffff;
      border: 1px solid rgba(0, 0, 0, 0.05);
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--primary-color, #386f99);
      font-size: 0.85rem;
      flex-shrink: 0;
    }

    .menu-card-title {
      font-size: 0.825rem;
      font-weight: 700;
      color: #1d1d1f;
      line-height: 1.2;

      .count-badge {
        font-size: 0.6rem;
        font-weight: 800;
        padding: 1px 5px;
        border-radius: 9999px;
        background: var(--primary-color, #386f99);
        color: #ffffff;
      }
    }

    .menu-card-desc {
      font-size: 0.68rem;
      color: #86868b;
      margin-top: 1px;
      line-height: 1.2;
    }

    .card-chevron {
      font-size: 0.68rem;
      color: #86868b;
      transition: transform 0.2s ease;
    }
  }

  .accordion-content {
    border-top: 1px solid rgba(0, 0, 0, 0.05);
    padding: 0.65rem;
    background: #f8fafc;
    max-height: 250px;
    overflow-y: auto;

    &::-webkit-scrollbar {
      width: 4px;
    }
    &::-webkit-scrollbar-thumb {
      background: #cbd5e1;
      border-radius: 4px;
    }
  }
}

/* Category Groups inside Mobile Accordion */
.category-group {
  .category-group-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.25rem 0.35rem;
    margin-bottom: 0.35rem;
  }

  .group-badge {
    font-size: 0.58rem;
    font-weight: 700;
    padding: 1px 5px;
    border-radius: 9999px;
    text-transform: uppercase;

    &--warning {
      background: #fef3c7;
      color: #92400e;
    }
    &--primary,
    &--info {
      background: #e0f2fe;
      color: #0369a1;
    }
  }
}

.category-item-link {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.38rem 0.5rem;
  border-radius: 8px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  text-decoration: none;
  min-width: 0;
  transition: all 0.15s ease;

  &:hover {
    border-color: var(--primary-color, #386f99);
    background: #f0f7fb;

    .item-name {
      color: var(--primary-color, #386f99);
    }
  }

  .item-icon-box {
    width: 24px;
    height: 24px;
    border-radius: 6px;
    background: #f5f5f7;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--primary-color, #386f99);
    font-size: 0.7rem;
    flex-shrink: 0;
  }

  .item-name {
    font-size: 0.75rem;
    font-weight: 700;
    color: #1d1d1f;
    line-height: 1.2;
    min-width: 0;
  }

  .item-badge {
    font-size: 0.55rem;
    font-weight: 700;
    padding: 1px 4px;
    border-radius: 4px;
    background: #e0f0fa;
    color: #20557c;
  }
}

/* Provider Container & Overlay */
.provider-logo-container,
.provider-choice-logo-box {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3px;
  flex-shrink: 0;

  .overlay-providers {
    background: #000000;
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
  }

  .provider-logo-img,
  .provider-choice-img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    position: relative;
    z-index: 12;
  }
}

.provider-choice-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.45rem 0.65rem;
  border-radius: 9px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: var(--primary-color, #386f99);
    background: #f0f7fb;
  }

  &--active {
    border-color: #86efac;
    background: #f0fdf4;
  }

  .provider-selected-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 0.65rem;
    font-weight: 700;
    color: #16a34a;
  }
}

/* Roulette Item */
.roulette-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.45rem 0.65rem;
  border-radius: 9px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: var(--primary-color, #386f99);
  }

  &--active {
    background: #f0fdf4;
    border-color: #86efac;
  }

  .roulette-thumb-box {
    width: 32px;
    height: 32px;
    border-radius: 7px;
    overflow: hidden;
    background: #0f172a;
    flex-shrink: 0;
  }
}
</style>
